// src/features/locker-types/components/LockerTypeEdit.tsx

import React, { useState } from 'react';
import { 
  Edit, 
  SimpleForm, 
  TextInput, 
  required, 
  useNotify, 
  useRedirect, 
  useRecordContext,
  LoadingIndicator,
  useTranslate,
} from 'react-admin';
import { 
  Grid,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { MyToolbar } from '../../../components/forms/MyToolbar';
import { MyFormContainer } from '../../../components/forms/MyFormContainer';
import { 
  CenteredFormLayout,
  MyFormSection 
} from '../../../components/forms/MyFormLayout';
import BaseDeleteDialog from '../../../components/common/BaseDeleteDialog';
import { LockerType } from '../types';
import { useLockerTypeValidation } from '../hooks/useLockerTypeValidation';

const LockerTypeEditTitle: React.FC = () => {
  const record = useRecordContext<LockerType>();
  const translate = useTranslate();
  
  if (!record) return <LoadingIndicator />;
  
  return (
    <span>
      {translate('resources.locker-types.titles.edit', 'Editar Tipo de Taquilla')}: {record.name}
    </span>
  );
};

const LockerTypeEdit: React.FC = (props) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Transform siguiendo el patrón de LockerControllerEdit
  const transform = (data: any) => {
    const record = data;
    return {
      name: data.name?.trim(),
      description: data.description?.trim(),
      hotelId: data.hotelId || record.hotel?.id, // Mantener el hotelId
    };
  };

  const handleSuccess = (data: any) => {
    notify('resources.locker-types.messages.update_success', { 
      type: 'success',
      messageArgs: { name: data?.name }
    });
    redirect('list', 'locker-types');
  };

  const handleError = (error: any) => {
    console.error('Mutation error:', error);
    notify(
      error?.body?.message || 
      error?.message ||
      'resources.locker-types.messages.update_error', 
      { type: 'error' }
    );
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  return (
    <Edit 
      {...props}
      title={<LockerTypeEditTitle />}
      transform={transform}
      mutationOptions={{
        onSuccess: handleSuccess,
        onError: handleError
      }}
      redirect={false}
      mutationMode='pessimistic'
    >
      <LockerTypeEditForm onDelete={handleDeleteClick} />
      
      <LockerTypeDeleteDialog 
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      />
    </Edit>
  );
};

const LockerTypeEditForm: React.FC<{ onDelete?: () => void }> = ({ onDelete }) => {
  const record = useRecordContext<LockerType>();
  const translate = useTranslate();
  const redirect = useRedirect();
  const { validateName, validateDescription } = useLockerTypeValidation();
  
  if (!record) return <LoadingIndicator />;
  
  const handleCancel = () => redirect('list', 'locker-types');

  return (
    <MyFormContainer>
      <SimpleForm
        toolbar={
          <MyToolbar 
            onCancel={handleCancel}
            resource="locker-types"
            showDelete={true}
            onDelete={onDelete}
          />
        }
      >
        <CenteredFormLayout>
          <MyFormSection 
            variant='outlined'
            title={translate('resources.locker-types.sections.details', 'Detalles del Tipo de Taquilla')}
            icon={<EditIcon />}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextInput 
                  source="hotel.name" 
                  label={translate('resources.locker-types.fields.hotel', 'Hotel')} 
                  fullWidth 
                  disabled 
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextInput 
                  source="name" 
                  label={translate('resources.locker-types.fields.name', 'Nombre del Tipo')} 
                  validate={[required(), validateName]} 
                  fullWidth 
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextInput 
                  source="description" 
                  label={translate('resources.locker-types.fields.description', 'Descripción')} 
                  multiline 
                  rows={3} 
                  validate={validateDescription} 
                  fullWidth 
                  size="small"
                />
              </Grid>
            </Grid>
          </MyFormSection>
        </CenteredFormLayout>
      </SimpleForm>
    </MyFormContainer>
  );
};

const LockerTypeDeleteDialog: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const record = useRecordContext<LockerType>();
  
  if (!record) return null;
  
  return (
    <BaseDeleteDialog
      open={open}
      onClose={onClose}
      recordId={record.id}
      resource="locker-types"
      warningMessage={undefined}
    />
  );
};

export default LockerTypeEdit;